import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Megaphone, XCircle, Mail } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PUBLIC = axios.create({ baseURL: `${BACKEND}/api` });

const POLL_INTERVAL_MS = 2500;
const MAX_POLL = 12; // ~30s

export default function SponsorBriefSuccess() {
  const { stationId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | paid | failed | expired
  const [brief, setBrief] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await PUBLIC.get(`/public/sponsor/${stationId}/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBrief(data.brief);
          setStatus("paid");
          return;
        }
        if (attempts.current >= MAX_POLL) {
          setStatus("expired");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempts.current >= MAX_POLL) {
          setStatus("failed");
        } else {
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, stationId]);

  return (
    <div className="min-h-screen bg-bg text-ink-primary flex items-center justify-center px-6 py-12" data-testid="sponsor-success-page">
      <div className="max-w-md w-full rounded-xl border border-line bg-bg-surface p-8 text-center">
        {status === "loading" && (
          <>
            <Loader2 className="h-10 w-10 text-brand-amber mx-auto mb-4 animate-spin" />
            <h1 className="font-display text-2xl font-bold mb-1">Confirming your payment…</h1>
            <p className="text-sm text-ink-muted">This usually takes 2–5 seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-brand-amber/15 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-8 w-8 text-brand-amber" />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80 mb-2">// BRIEF RECEIVED</div>
            <h1 className="font-display text-3xl font-black tracking-tighter mb-2">You&apos;re on the air list.</h1>
            <p className="text-sm text-ink-secondary mb-6">
              Thanks{brief?.sponsor_name ? `, ${brief.sponsor_name.split(" ")[0]}` : ""}! We&apos;ve received your brief for
              <span className="text-ink-primary"> {brief?.advertiser || "your spot"}</span>. The station team will
              produce your {brief?.duration_sec || ""}s commercial and email you for approval before it goes live.
            </p>
            <div className="rounded-md border border-line bg-bg-elevated p-3 text-xs text-ink-muted flex items-center gap-2 justify-center" data-testid="sponsor-success-email">
              <Mail className="h-3.5 w-3.5 text-brand-amber" />
              Confirmation sent to <span className="text-ink-primary">{brief?.sponsor_email}</span>
            </div>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <XCircle className="h-10 w-10 text-brand-onair mx-auto mb-3" />
            <h1 className="font-display text-2xl font-bold mb-1">Hmm, we couldn&apos;t confirm payment</h1>
            <p className="text-sm text-ink-muted mb-4">
              {status === "expired"
                ? "Stripe is taking longer than usual. Refresh this page in a moment."
                : "Try refreshing, or contact the station directly if you were charged."}
            </p>
            <a href={`/sponsor/${stationId}`} className="inline-block rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm hover:text-cyan-300 transition">
              <Megaphone className="h-3.5 w-3.5 inline mr-1.5" /> Back to the brief
            </a>
          </>
        )}
      </div>
    </div>
  );
}
